<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage NIRA integration username and password used by the integration service.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadCredentials">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
      </div>
    </div>

    <div class="relative min-h-[280px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading NIRA credentials...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="mb-4">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="credentialsConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
          >
            {{ credentialsConfigured ? "Credentials configured" : "No credentials configured" }}
          </span>
        </div>

        <form class="max-w-lg space-y-4" @submit.prevent="saveCredentials">
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Username</label>
            <input
              v-model="form.username"
              type="text"
              autocomplete="off"
              placeholder="Enter NIRA username"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 mt-1"
              :disabled="loading || saving"
              required
            />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Password</label>
            <div class="relative mt-1">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Enter NIRA password"
                class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                :disabled="loading || saving"
                required
              />
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                :title="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <button type="submit" class="btn-primary" :disabled="loading || saving || !allFieldsEdited">
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-save"></i>
              <span>{{ credentialsConfigured ? "Update credentials" : "Save credentials" }}</span>
            </button>
            <span v-if="!allFieldsEdited" class="text-xs text-gray-500">
              Edit both the username and password to save.
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useToast } from "@/composables";
import {
  extractErrorMessage,
  fetchNiraCredentials,
  saveNiraCredentials,
} from "./niraApi";
import { useAllFieldsEdited } from "./useAllFieldsEdited";

const { successToast, errorToast, warningToast } = useToast();

const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const credentialsConfigured = ref(false);
const showPassword = ref(false);
const form = ref({
  username: "",
  password: "",
});
const { allFieldsEdited, markPristine } = useAllFieldsEdited(form);

const applyCredentialsToForm = (
  username: string | null,
  password: string | null,
  configured: boolean
) => {
  credentialsConfigured.value = configured;
  form.value.username = username || "";
  form.value.password = password || "";
  markPristine();
};

const loadCredentials = async () => {
  loading.value = true;
  try {
    const credentials = await fetchNiraCredentials();
    applyCredentialsToForm(
      credentials.username,
      credentials.password,
      credentials.configured
    );
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      applyCredentialsToForm(null, null, false);
    } else {
      errorToast("Error", extractErrorMessage(error, "Could not load NIRA credentials"));
    }
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const saveCredentials = async () => {
  if (!allFieldsEdited.value) {
    warningToast("Validation", "Edit both the username and password before saving");
    return;
  }
  const username = form.value.username.trim();
  const password = form.value.password.trim();
  saving.value = true;
  try {
    await saveNiraCredentials({ username, password });
    successToast("Success", "NIRA credentials saved");
    await loadCredentials();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save NIRA credentials"));
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadCredentials();
});

defineExpose({ refresh: loadCredentials });
</script>
